import type { NetOrderItem } from '@ims/core'

// A round: what a waiter has tapped on the menu and not added to the order
// yet. It lives on this device only, so a wrong tap costs nothing: order items
// are append-only, and an item saved by mistake can only be cancelled with a
// reason. Whole portions, by product id.

export type Round = Readonly<Record<string, number>>

export function addToRound(round: Round, productId: string): Round {
  return { ...round, [productId]: (round[productId] ?? 0) + 1 }
}

/** One less of a dish; the dish leaves the round at zero. */
export function removeFromRound(round: Round, productId: string): Round {
  const { [productId]: qty = 0, ...rest } = round
  return qty > 1 ? { ...rest, [productId]: qty - 1 } : rest
}

/** How many portions are in the round. */
export function roundCount(round: Round): number {
  return Object.values(round).reduce((sum, qty) => sum + qty, 0)
}

/** What the round comes to at the menu's prices (a product that is gone counts for nothing). */
export function roundTotal(
  round: Round,
  products: readonly { id: string; sell_price_paisa: number }[],
): number {
  const price = new Map(products.map((p) => [p.id, p.sell_price_paisa]))
  return Object.entries(round).reduce((sum, [id, qty]) => sum + (price.get(id) ?? 0) * qty, 0)
}

/** Portions already on the order, by product (quantities are milli-units). */
export function orderedByProduct(left: readonly NetOrderItem[]): Map<string, number> {
  const out = new Map<string, number>()
  for (const n of left) {
    if (!n.item.product_id) continue
    out.set(n.item.product_id, (out.get(n.item.product_id) ?? 0) + n.qtyMilli)
  }
  return out
}

/** "1 item", "4 items". */
export function itemsLabel(count: number): string {
  return `${count} ${count === 1 ? 'item' : 'items'}`
}

// A waiting round is kept per order in this browser, so leaving the table and
// coming back (or a reload) does not lose it.

const key = (orderId: string) => `ims.round.${orderId}`

/** Keeps only well-formed entries: an id with a whole number above zero. */
export function parseRound(text: string | null): Round {
  try {
    const value: unknown = JSON.parse(text ?? '{}')
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
    const out: Record<string, number> = {}
    for (const [id, qty] of Object.entries(value)) {
      if (typeof qty === 'number' && Number.isInteger(qty) && qty > 0 && qty <= 99) out[id] = qty
    }
    return out
  } catch {
    return {}
  }
}

export function loadRound(orderId: string): Round {
  try {
    return parseRound(localStorage.getItem(key(orderId)))
  } catch {
    return {}
  }
}

export function saveRound(orderId: string, round: Round): void {
  try {
    if (roundCount(round) === 0) localStorage.removeItem(key(orderId))
    else localStorage.setItem(key(orderId), JSON.stringify(round))
  } catch {
    // Private mode or a full disk: the round still works until the page is left.
  }
}

// A custom item typed into the round (docs/plans/custom-order-items.md): a dish that
// isn't on the menu, kept with the round on this device until it is added to the order.

export interface CustomPick {
  id: string
  description: string
  qtyMilli: number
  unitPricePaisa: number
  priceIncludesTax: boolean
  taxRateId: string | null
  taxRateBp: number
  station: 'kitchen' | 'bar' | 'none'
}

/** Portions of custom items in the round (a quantity of 1.5 counts as one line). */
export function customCount(picks: readonly CustomPick[]): number {
  return picks.reduce((sum, p) => sum + Math.max(1, Math.round(p.qtyMilli / 1000)), 0)
}

/** What the custom items come to, as typed (the price for one times the quantity). */
export function customTotal(picks: readonly CustomPick[]): number {
  return picks.reduce((sum, p) => sum + Math.round((p.unitPricePaisa * p.qtyMilli) / 1000), 0)
}

const STATIONS = new Set(['kitchen', 'bar', 'none'])

/** Keeps only well-formed picks (the text comes from this browser's storage). */
export function parseCustoms(text: string | null): CustomPick[] {
  try {
    const value: unknown = JSON.parse(text ?? '[]')
    if (!Array.isArray(value)) return []
    return value.filter((p): p is CustomPick => {
      if (!p || typeof p !== 'object') return false
      const c = p as Record<string, unknown>
      return (
        typeof c.id === 'string' &&
        typeof c.description === 'string' &&
        c.description.trim() !== '' &&
        Number.isSafeInteger(c.qtyMilli) &&
        (c.qtyMilli as number) > 0 &&
        Number.isSafeInteger(c.unitPricePaisa) &&
        (c.unitPricePaisa as number) > 0 &&
        typeof c.priceIncludesTax === 'boolean' &&
        (c.taxRateId === null || typeof c.taxRateId === 'string') &&
        Number.isSafeInteger(c.taxRateBp) &&
        STATIONS.has(c.station as string)
      )
    })
  } catch {
    return []
  }
}

const customKey = (orderId: string) => `ims.round-custom.${orderId}`

export function loadCustoms(orderId: string): CustomPick[] {
  try {
    return parseCustoms(localStorage.getItem(customKey(orderId)))
  } catch {
    return []
  }
}

export function saveCustoms(orderId: string, picks: readonly CustomPick[]): void {
  try {
    if (picks.length === 0) localStorage.removeItem(customKey(orderId))
    else localStorage.setItem(customKey(orderId), JSON.stringify(picks))
  } catch {
    // Private mode or a full disk: the picks still work until the page is left.
  }
}
